import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useBillboardDetail } from '@/features/billboards/api/useBillboardQueries';
import { clearStandardCampaignDraft } from '@/features/campaigns/utils/standardCampaignDraftStorage';
import {
  buildFullAddress,
  resolveBillboardMedia,
} from '@/features/billboards/utils/billboardDetailUtils';
import { DETAIL_AUTO_SLIDE_MS } from '@/features/billboards/constants/detailTheme';
import { getBillboardCoordinates } from '@/shared/lib/maps/googleMaps';
import { useInstantAdAccess } from '@/features/profile/hooks/useInstantAdAccess';
import {
  todayIsoDate,
  tomorrowIsoDate,
} from '@/features/campaigns/utils/campaignUtils';
import { useAppSelector } from '@/shared/store/hooks';
import { getAccessToken } from '@/shared/lib/storage/token';
import type { BillboardCreateLaunchState } from '@/features/campaigns/types/billboardCreateLaunch';
import { isClientRole } from '@/shared/config/roles';
import { getCurrencyCode } from '@/shared/lib/format/money';

export function useBillboardDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const query = useBillboardDetail(id);
  const billboard = query.data?.data;
  const isAuthenticated = useAppSelector((s) => s.auth.isAuthenticated);
  const role = useAppSelector((s) => s.auth.user?.role);
  const isClient = isClientRole(role);
  const hasToken = Boolean(getAccessToken()) || isAuthenticated;

  const [startDate, setStartDate] = useState('');
  const [selectedImage, setSelectedImage] = useState(0);

  const media = useMemo(
    () => (billboard ? resolveBillboardMedia(billboard) : []),
    [billboard],
  );

  const currency = useMemo(() => getCurrencyCode(), []);
  const coordinates = useMemo(
    () => (billboard ? getBillboardCoordinates(billboard) : null),
    [billboard],
  );
  const fullAddress = useMemo(
    () => (billboard ? buildFullAddress(billboard) : ''),
    [billboard],
  );

  // Same-day launches need instant-ad access on a screen that is currently online.
  const hasInstantAdAccess = useInstantAdAccess();
  const isScreenOffline =
    String(billboard?.deviceId?.deviceStatus || '').toUpperCase() === 'OFFLINE';
  const allowToday = hasInstantAdAccess && !isScreenOffline;
  const minStartDate = allowToday ? todayIsoDate() : tomorrowIsoDate();
  const canCreate = isClient
    ? true
    : Boolean(startDate) && startDate >= minStartDate;
  const activeMedia = media[selectedImage];

  useEffect(() => {
    setSelectedImage(0);
  }, [id]);

  useEffect(() => {
    if (media.length <= 1) return;
    const timer = window.setTimeout(() => {
      setSelectedImage((prev) => (prev < media.length - 1 ? prev + 1 : 0));
    }, DETAIL_AUTO_SLIDE_MS);
    return () => window.clearTimeout(timer);
  }, [media.length, selectedImage]);

  const handleCreateCampaign = () => {
    if (!hasToken) {
      navigate('/login', {
        state: { from: location.pathname || `/billboard/${id}` },
      });
      return;
    }

    if (isClient) {
      navigate('/campaigns', { state: { openCampaignCreate: true } });
      return;
    }

    if (!canCreate || !id || !billboard) return;

    clearStandardCampaignDraft();

    const address = billboard.address;
    const state: BillboardCreateLaunchState = {
      from: 'billboard',
      billboardId: id,
      startDate,
      endDate: startDate,
      city: address?.city || '',
      screenName: billboard.screenName || 'Screen',
      thumbnail: billboard.thumbnail?.url || '',
      addressLine: [address?.building, address?.city, address?.state]
        .filter(Boolean)
        .join(' / '),
    };

    navigate('/campaigns/create/standard', { state });
  };

  return {
    id,
    navigate,
    billboard,
    isPending: query.isPending,
    isError: query.isError,
    hasToken,
    isClient,
    startDate,
    setStartDate,
    minStartDate,
    hasInstantAdAccess,
    isScreenOffline,
    selectedImage,
    setSelectedImage,
    media,
    activeMedia,
    currency,
    coordinates,
    fullAddress,
    canCreate,
    handleCreateCampaign,
  };
}
